import { useEffect, useState } from "react";
import { Sparkles, Target, Users, Compass, CheckCircle2 } from "lucide-react";
import api from "@/api/client";
import { useSiteContent } from "@/context/SiteContentContext";

const MISSION_POINTS = [
  "Empowering exhibition sellers, homegrown brands, designers, and small businesses with a simple and affordable digital selling platform.",
  "Delivering carefully curated fashion, lifestyle, gifts, kids' products, accessories, and home collections instead of overwhelming customers with endless choices.",
  "Creating urgency and excitement through daily limited-time exhibitions and exclusive product drops.",
  "Helping customers discover unique products they cannot easily find on conventional e-commerce platforms.",
  "Building trust through verified exhibitors, secure payments, transparent policies, and reliable customer support.",
  "Leveraging technology, AI, and data-driven insights to enhance the shopping experience for both buyers and sellers.",
  "Supporting the growth of India's entrepreneurial ecosystem by giving local brands nationwide visibility and opportunities to scale.",
  "Continuously innovating to make CityShor Pop-Up Bazaar the preferred destination for discovering fresh collections every day.",
];

function MemberCard({ m }) {
  const initials = m.name.split(" ").map((n) => n[0]).join("").slice(0, 2);
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6" data-testid={`team-${m.name}`}>
      {m.photo_base64 ? (
        <img src={m.photo_base64} alt={m.name} className="h-16 w-16 rounded-full object-cover mb-4" />
      ) : (
        <div className="h-16 w-16 rounded-full bg-[#FEF3E6] flex items-center justify-center mb-4">
          <span className="font-display text-xl text-[#F48E2E]">{initials}</span>
        </div>
      )}
      <div className="font-display text-xl">{m.name}</div>
      {m.designation && <div className="text-sm text-gray-500 mt-1">{m.designation}</div>}
      {m.bio && <p className="text-sm text-gray-600 mt-3 leading-relaxed">{m.bio}</p>}
    </div>
  );
}

export default function About() {
  const { t } = useSiteContent();
  const [members, setMembers] = useState([]);

  useEffect(() => {
    api.get("/team").then((r) => setMembers(r.data || [])).catch(() => setMembers([]));
  }, []);

  const founders = members.filter((m) => m.type === "founder");
  const team = members.filter((m) => m.type !== "founder");
  const showTeamHeading = founders.length > 0 && team.length > 0;

  return (
    <div className="mx-auto max-w-6xl px-4 lg:px-8 py-14" data-testid="about-page">
      <div className="text-center mb-14">
        <div className="text-xs uppercase tracking-[0.3em] text-[#F48E2E] font-bold mb-2">{t("about.kicker", "About CityShor")}</div>
        <h1 className="font-display text-4xl md:text-5xl mb-4">{t("about.headline", "Where offline exhibitors meet online buyers.")}</h1>
        <p className="text-gray-600 max-w-2xl mx-auto">
          {t("about.tagline", "A curated daily-drop marketplace that gives small Indian exhibitors nationwide reach, and helps buyers find products they would not usually see on other online stores.")}
        </p>
      </div>

      <section id="vision" className="mb-14" data-testid="section-vision">
        <div className="grid md:grid-cols-[auto_1fr] gap-6 items-start p-8 md:p-10 bg-white rounded-3xl border border-gray-200">
          <div className="h-14 w-14 rounded-full bg-[#FEF3E6] flex items-center justify-center">
            <Target className="h-6 w-6 text-[#F48E2E]" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#F48E2E] font-bold mb-2">Vision</div>
            <h2 className="font-display text-3xl md:text-4xl mb-4">Vision</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              To become India's most trusted online lifestyle exhibition platform, connecting unique offline brands with buyers across the country through curated, time-limited shopping experiences. We envision a future where every small business, designer, artisan, and exhibition seller can showcase their products nationally without the complexity or cost of building their own e-commerce store.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Our goal is to redefine online shopping by making discovery exciting, supporting local entrepreneurs, and ensuring that every visit brings something new, exclusive, and worth exploring.
            </p>
          </div>
        </div>
      </section>

      <section id="mission" className="mb-14" data-testid="section-mission">
        <div className="p-8 md:p-10 bg-white rounded-3xl border border-gray-200">
          <div className="flex items-center gap-4 mb-5">
            <div className="h-14 w-14 rounded-full bg-[#FEF3E6] flex items-center justify-center">
              <Sparkles className="h-6 w-6 text-[#F48E2E]" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-[#F48E2E] font-bold mb-1">Mission</div>
              <h2 className="font-display text-3xl md:text-4xl">Mission</h2>
            </div>
          </div>
          <p className="text-gray-700 leading-relaxed mb-6">
            Our mission is to bring the excitement of offline lifestyle exhibitions online by creating a curated platform where quality products, limited-time collections, and trusted exhibitors come together.
          </p>
          <div className="text-sm font-semibold uppercase tracking-widest text-gray-500 mb-3">We are committed to:</div>
          <ul className="space-y-3">
            {MISSION_POINTS.map((p, i) => (
              <li key={i} className="flex gap-3">
                <CheckCircle2 className="h-5 w-5 text-[#F48E2E] flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 leading-relaxed">{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section id="purpose" className="mb-16" data-testid="section-purpose">
        <div className="grid md:grid-cols-[auto_1fr] gap-6 items-start p-8 md:p-10 bg-gradient-to-br from-[#F48E2E] to-[#111827] rounded-3xl text-white">
          <div className="h-14 w-14 rounded-full bg-white/20 flex items-center justify-center">
            <Compass className="h-6 w-6 text-white" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.3em] font-bold mb-2 text-white/90">Our Purpose</div>
            <h2 className="font-display text-3xl md:text-4xl mb-4">Our Purpose</h2>
            <p className="leading-relaxed mb-4 text-white/95">
              We believe extraordinary products shouldn't remain limited to a single exhibition or city. By bringing offline exhibitions online, we help small businesses reach customers across India while giving shoppers access to exclusive collections that are available only for a limited time.
            </p>
            <p className="leading-relaxed text-white/95">
              CityShor Pop-Up Bazaar exists to transform every day into a new shopping exhibition where discovery, exclusivity, and entrepreneurship come together.
            </p>
          </div>
        </div>
      </section>

      <section id="team" data-testid="section-team">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-[0.3em] text-[#F48E2E] font-bold mb-2">Meet the team</div>
          <h2 className="font-display text-3xl md:text-4xl">{founders.length > 0 ? "Founders" : "Our Team"}</h2>
        </div>

        {founders.length > 0 && (
          <div data-testid="founders-grid" className="grid sm:grid-cols-2 gap-4 max-w-4xl">
            {founders.map((m) => <MemberCard key={m.id} m={m} />)}
          </div>
        )}

        {team.length > 0 && (
          <div className="mt-10">
            {showTeamHeading && <h3 className="font-display text-2xl mb-4">Team</h3>}
            <div data-testid="team-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {team.map((m) => <MemberCard key={m.id} m={m} />)}
            </div>
          </div>
        )}

        {members.length === 0 && (
          <div data-testid="team-empty" className="text-sm text-gray-500 rounded-2xl border border-dashed border-gray-200 p-8 text-center max-w-3xl">
            Team profiles will appear here soon.
          </div>
        )}

        <div className="mt-6 flex items-center gap-3 p-5 bg-white rounded-2xl border border-gray-200 max-w-3xl">
          <Users className="h-5 w-5 text-[#F48E2E]" />
          <p className="text-sm text-gray-700">Supported by a growing team of curators, community managers, technology and logistics partners across India.</p>
        </div>
      </section>
    </div>
  );
}
